/**
 * MLB Roster Service
 *
 * Provides MLB team roster data with player information including MLB IDs for headshots.
 * Integrates with mlbHeadshotService for player images.
 *
 * @example
 * import { getMLBTeamRoster, MLB_TEAMS } from './mlbRosterService';
 * const yankees = getMLBTeamRoster('yankees');
 */

import { getMLBHeadshotUrl } from './mlbHeadshotService';

// =============================================================================
// MLB TEAM DEFINITIONS
// =============================================================================

export const MLB_TEAMS = {
  // ==================== AMERICAN LEAGUE EAST ====================
  yankees: {
    teamId: 'yankees',
    mlbTeamId: 147,
    name: 'New York Yankees',
    abbreviation: 'NYY',
    division: 'AL East',
    league: 'American League',
    primaryColor: '#003087',
    secondaryColor: '#C4CED4',
    logoUrl: '/logos/yankees-ny.png',
  },
  orioles: {
    teamId: 'orioles',
    mlbTeamId: 110,
    name: 'Baltimore Orioles',
    abbreviation: 'BAL',
    division: 'AL East',
    league: 'American League',
    primaryColor: '#DF4601',
    secondaryColor: '#000000',
    logoUrl: '/logos/orioles-o.png',
  },
  redsox: {
    teamId: 'redsox',
    mlbTeamId: 111,
    name: 'Boston Red Sox',
    abbreviation: 'BOS',
    division: 'AL East',
    league: 'American League',
    primaryColor: '#BD3039',
    secondaryColor: '#0C2340',
    logoUrl: '/logos/redsox.png',
  },
  rays: {
    teamId: 'rays',
    mlbTeamId: 139,
    name: 'Tampa Bay Rays',
    abbreviation: 'TB',
    division: 'AL East',
    league: 'American League',
    primaryColor: '#092C5C',
    secondaryColor: '#8FBCE6',
    logoUrl: '/logos/rays.png',
  },
  bluejays: {
    teamId: 'bluejays',
    mlbTeamId: 141,
    name: 'Toronto Blue Jays',
    abbreviation: 'TOR',
    division: 'AL East',
    league: 'American League',
    primaryColor: '#134A8E',
    secondaryColor: '#1D2D5C',
    logoUrl: '/logos/bluejays.png',
  },
  // ==================== AMERICAN LEAGUE CENTRAL ====================
  tigers: {
    teamId: 'tigers',
    mlbTeamId: 116,
    name: 'Detroit Tigers',
    abbreviation: 'DET',
    division: 'AL Central',
    league: 'American League',
    primaryColor: '#0C2C56',
    secondaryColor: '#FA4616',
    logoUrl: '/logos/tigers-d.png',
  },
  guardians: {
    teamId: 'guardians',
    mlbTeamId: 114,
    name: 'Cleveland Guardians',
    abbreviation: 'CLE',
    division: 'AL Central',
    league: 'American League',
    primaryColor: '#00385D',
    secondaryColor: '#E50022',
    logoUrl: '/logos/guardians.png',
  },
  whitesox: {
    teamId: 'whitesox',
    mlbTeamId: 145,
    name: 'Chicago White Sox',
    abbreviation: 'CWS',
    division: 'AL Central',
    league: 'American League',
    primaryColor: '#27251F',
    secondaryColor: '#C4CED4',
    logoUrl: '/logos/whitesox.png',
  },
  royals: {
    teamId: 'royals',
    mlbTeamId: 118,
    name: 'Kansas City Royals',
    abbreviation: 'KC',
    division: 'AL Central',
    league: 'American League',
    primaryColor: '#004687',
    secondaryColor: '#BD9B60',
    logoUrl: '/logos/royals.png',
  },
  twins: {
    teamId: 'twins',
    mlbTeamId: 142,
    name: 'Minnesota Twins',
    abbreviation: 'MIN',
    division: 'AL Central',
    league: 'American League',
    primaryColor: '#002B5C',
    secondaryColor: '#D31145',
    logoUrl: '/logos/twins.png',
  },
  // ==================== AMERICAN LEAGUE WEST ====================
  athletics: {
    teamId: 'athletics',
    mlbTeamId: 133,
    name: 'Oakland Athletics',
    abbreviation: 'OAK',
    division: 'AL West',
    league: 'American League',
    primaryColor: '#003831',
    secondaryColor: '#EFB21E',
    logoUrl: '/logos/athletics-a.png',
  },
  astros: {
    teamId: 'astros',
    mlbTeamId: 117,
    name: 'Houston Astros',
    abbreviation: 'HOU',
    division: 'AL West',
    league: 'American League',
    primaryColor: '#002D62',
    secondaryColor: '#EB6E1F',
    logoUrl: '/logos/astros.png',
  },
  angels: {
    teamId: 'angels',
    mlbTeamId: 108,
    name: 'Los Angeles Angels',
    abbreviation: 'LAA',
    division: 'AL West',
    league: 'American League',
    primaryColor: '#BA0021',
    secondaryColor: '#003263',
    logoUrl: '/logos/angels.png',
  },
  mariners: {
    teamId: 'mariners',
    mlbTeamId: 136,
    name: 'Seattle Mariners',
    abbreviation: 'SEA',
    division: 'AL West',
    league: 'American League',
    primaryColor: '#0C2C56',
    secondaryColor: '#005C5C',
    logoUrl: '/logos/mariners.png',
  },
  rangers: {
    teamId: 'rangers',
    mlbTeamId: 140,
    name: 'Texas Rangers',
    abbreviation: 'TEX',
    division: 'AL West',
    league: 'American League',
    primaryColor: '#003278',
    secondaryColor: '#C0111F',
    logoUrl: '/logos/rangers.png',
  },
  // ==================== NATIONAL LEAGUE EAST ====================
  braves: {
    teamId: 'braves',
    mlbTeamId: 144,
    name: 'Atlanta Braves',
    abbreviation: 'ATL',
    division: 'NL East',
    league: 'National League',
    primaryColor: '#13274F',
    secondaryColor: '#CE1141',
    logoUrl: '/logos/braves.png',
  },
  marlins: {
    teamId: 'marlins',
    mlbTeamId: 146,
    name: 'Miami Marlins',
    abbreviation: 'MIA',
    division: 'NL East',
    league: 'National League',
    primaryColor: '#00A3E0',
    secondaryColor: '#EF3340',
    logoUrl: '/logos/marlins.png',
  },
  mets: {
    teamId: 'mets',
    mlbTeamId: 121,
    name: 'New York Mets',
    abbreviation: 'NYM',
    division: 'NL East',
    league: 'National League',
    primaryColor: '#002D72',
    secondaryColor: '#FF5910',
    logoUrl: '/logos/mets.png',
  },
  phillies: {
    teamId: 'phillies',
    mlbTeamId: 143,
    name: 'Philadelphia Phillies',
    abbreviation: 'PHI',
    division: 'NL East',
    league: 'National League',
    primaryColor: '#E81828',
    secondaryColor: '#002D72',
    logoUrl: '/logos/phillies.png',
  },
  nationals: {
    teamId: 'nationals',
    mlbTeamId: 120,
    name: 'Washington Nationals',
    abbreviation: 'WSH',
    division: 'NL East',
    league: 'National League',
    primaryColor: '#AB0003',
    secondaryColor: '#14225A',
    logoUrl: '/logos/nationals.png',
  },
  // ==================== NATIONAL LEAGUE CENTRAL ====================
  cubs: {
    teamId: 'cubs',
    mlbTeamId: 112,
    name: 'Chicago Cubs',
    abbreviation: 'CHC',
    division: 'NL Central',
    league: 'National League',
    primaryColor: '#0E3386',
    secondaryColor: '#CC3433',
    logoUrl: '/logos/cubs.png',
  },
  reds: {
    teamId: 'reds',
    mlbTeamId: 113,
    name: 'Cincinnati Reds',
    abbreviation: 'CIN',
    division: 'NL Central',
    league: 'National League',
    primaryColor: '#C6011F',
    secondaryColor: '#000000',
    logoUrl: '/logos/reds.png',
  },
  brewers: {
    teamId: 'brewers',
    mlbTeamId: 158,
    name: 'Milwaukee Brewers',
    abbreviation: 'MIL',
    division: 'NL Central',
    league: 'National League',
    primaryColor: '#12284B',
    secondaryColor: '#B6922E',
    logoUrl: '/logos/brewers.png',
  },
  pirates: {
    teamId: 'pirates',
    mlbTeamId: 134,
    name: 'Pittsburgh Pirates',
    abbreviation: 'PIT',
    division: 'NL Central',
    league: 'National League',
    primaryColor: '#27251F',
    secondaryColor: '#FDB827',
    logoUrl: '/logos/pirates.png',
  },
  cardinals: {
    teamId: 'cardinals',
    mlbTeamId: 138,
    name: 'St. Louis Cardinals',
    abbreviation: 'STL',
    division: 'NL Central',
    league: 'National League',
    primaryColor: '#C41E3A',
    secondaryColor: '#0C2340',
    logoUrl: '/logos/cardinals.png',
  },
  // ==================== NATIONAL LEAGUE WEST ====================
  diamondbacks: {
    teamId: 'diamondbacks',
    mlbTeamId: 109,
    name: 'Arizona Diamondbacks',
    abbreviation: 'ARI',
    division: 'NL West',
    league: 'National League',
    primaryColor: '#A71930',
    secondaryColor: '#E3D4AD',
    logoUrl: '/logos/diamondbacks.png',
  },
  rockies: {
    teamId: 'rockies',
    mlbTeamId: 115,
    name: 'Colorado Rockies',
    abbreviation: 'COL',
    division: 'NL West',
    league: 'National League',
    primaryColor: '#33006F',
    secondaryColor: '#C4CED4',
    logoUrl: '/logos/rockies.png',
  },
  dodgers: {
    teamId: 'dodgers',
    mlbTeamId: 119,
    name: 'Los Angeles Dodgers',
    abbreviation: 'LAD',
    division: 'NL West',
    league: 'National League',
    primaryColor: '#005A9C',
    secondaryColor: '#EF3E42',
    logoUrl: '/logos/dodgers.png',
  },
  padres: {
    teamId: 'padres',
    mlbTeamId: 135,
    name: 'San Diego Padres',
    abbreviation: 'SD',
    division: 'NL West',
    league: 'National League',
    primaryColor: '#2F241D',
    secondaryColor: '#FFC425',
    logoUrl: '/logos/padres.png',
  },
  giants: {
    teamId: 'giants',
    mlbTeamId: 137,
    name: 'San Francisco Giants',
    abbreviation: 'SF',
    division: 'NL West',
    league: 'National League',
    primaryColor: '#FD5A1E',
    secondaryColor: '#27251F',
    logoUrl: '/logos/giants.png',
  },
};

// =============================================================================
// ABBREVIATION TO TEAM KEY MAPPING
// =============================================================================

export const MLB_ABBREV_TO_KEY = {
  // AL East
  'nyy': 'yankees',
  'yankees': 'yankees',
  'bal': 'orioles',
  'orioles': 'orioles',
  'bos': 'redsox',
  'redsox': 'redsox',
  'red sox': 'redsox',
  'tb': 'rays',
  'rays': 'rays',
  'tbr': 'rays',
  'tor': 'bluejays',
  'bluejays': 'bluejays',
  'blue jays': 'bluejays',
  // AL Central
  'det': 'tigers',
  'tigers': 'tigers',
  'cle': 'guardians',
  'guardians': 'guardians',
  'cws': 'whitesox',
  'whitesox': 'whitesox',
  'white sox': 'whitesox',
  'chw': 'whitesox',
  'kc': 'royals',
  'kcr': 'royals',
  'royals': 'royals',
  'min': 'twins',
  'twins': 'twins',
  // AL West
  'oak': 'athletics',
  'athletics': 'athletics',
  'hou': 'astros',
  'astros': 'astros',
  'laa': 'angels',
  'ana': 'angels',
  'angels': 'angels',
  'sea': 'mariners',
  'mariners': 'mariners',
  'tex': 'rangers',
  'rangers': 'rangers',
  // NL East
  'atl': 'braves',
  'braves': 'braves',
  'mia': 'marlins',
  'fla': 'marlins',
  'marlins': 'marlins',
  'nym': 'mets',
  'mets': 'mets',
  'phi': 'phillies',
  'phillies': 'phillies',
  'wsh': 'nationals',
  'was': 'nationals',
  'nationals': 'nationals',
  // NL Central
  'chc': 'cubs',
  'cubs': 'cubs',
  'cin': 'reds',
  'reds': 'reds',
  'mil': 'brewers',
  'brewers': 'brewers',
  'pit': 'pirates',
  'pirates': 'pirates',
  'stl': 'cardinals',
  'cardinals': 'cardinals',
  // NL West
  'ari': 'diamondbacks',
  'arz': 'diamondbacks',
  'diamondbacks': 'diamondbacks',
  'col': 'rockies',
  'rockies': 'rockies',
  'lad': 'dodgers',
  'la': 'dodgers',
  'dodgers': 'dodgers',
  'sd': 'padres',
  'sdp': 'padres',
  'padres': 'padres',
  'sf': 'giants',
  'sfg': 'giants',
  'giants': 'giants',
};

// =============================================================================
// MLB PLAYER ROSTERS (2024 Season Data with MLB IDs for headshots)
// =============================================================================

const MLB_ROSTERS = {
  yankees: [
    // Pitchers
    { mlbId: 543135, firstName: 'Gerrit', lastName: 'Cole', position: 'SP', number: 45, throws: 'R', bats: 'R', height: '6-4', weight: 220 },
    { mlbId: 664285, firstName: 'Carlos', lastName: 'Rodon', position: 'SP', number: 55, throws: 'L', bats: 'L', height: '6-3', weight: 245 },
    { mlbId: 621237, firstName: 'Marcus', lastName: 'Stroman', position: 'SP', number: 0, throws: 'R', bats: 'R', height: '5-7', weight: 180 },
    { mlbId: 666142, firstName: 'Luis', lastName: 'Gil', position: 'SP', number: 81, throws: 'R', bats: 'R', height: '6-2', weight: 185 },
    { mlbId: 547943, firstName: 'Nestor', lastName: 'Cortes', position: 'SP', number: 65, throws: 'L', bats: 'R', height: '5-10', weight: 210 },
    { mlbId: 592332, firstName: 'Clay', lastName: 'Holmes', position: 'RP', number: 35, throws: 'R', bats: 'R', height: '6-5', weight: 245 },
    { mlbId: 622253, firstName: 'Jonathan', lastName: 'Loaisiga', position: 'RP', number: 43, throws: 'R', bats: 'R', height: '5-11', weight: 165 },
    { mlbId: 656844, firstName: 'Luke', lastName: 'Weaver', position: 'RP', number: 27, throws: 'R', bats: 'R', height: '6-2', weight: 170 },
    { mlbId: 666152, firstName: 'Tommy', lastName: 'Kahnle', position: 'RP', number: 48, throws: 'R', bats: 'R', height: '6-1', weight: 245 },
    { mlbId: 670261, firstName: 'Ian', lastName: 'Hamilton', position: 'RP', number: 71, throws: 'R', bats: 'R', height: '6-0', weight: 200 },
    // Catchers
    { mlbId: 596115, firstName: 'Jose', lastName: 'Trevino', position: 'C', number: 39, throws: 'R', bats: 'R', height: '5-11', weight: 195 },
    { mlbId: 663728, firstName: 'Austin', lastName: 'Wells', position: 'C', number: 28, throws: 'R', bats: 'L', height: '6-2', weight: 220 },
    // Infielders
    { mlbId: 593934, firstName: 'Anthony', lastName: 'Rizzo', position: '1B', number: 48, throws: 'L', bats: 'L', height: '6-3', weight: 240 },
    { mlbId: 676475, firstName: 'Anthony', lastName: 'Volpe', position: 'SS', number: 11, throws: 'R', bats: 'R', height: '5-11', weight: 180 },
    { mlbId: 650402, firstName: 'Gleyber', lastName: 'Torres', position: '2B', number: 25, throws: 'R', bats: 'R', height: '6-1', weight: 205 },
    { mlbId: 666182, firstName: 'DJ', lastName: 'LeMahieu', position: '2B', number: 26, throws: 'R', bats: 'R', height: '6-4', weight: 220 },
    { mlbId: 663624, firstName: 'Oswaldo', lastName: 'Cabrera', position: '3B', number: 95, throws: 'R', bats: 'S', height: '5-10', weight: 190 },
    { mlbId: 682998, firstName: 'Oswald', lastName: 'Peraza', position: 'SS', number: 91, throws: 'R', bats: 'R', height: '6-0', weight: 165 },
    // Outfielders
    { mlbId: 592450, firstName: 'Aaron', lastName: 'Judge', position: 'RF', number: 99, throws: 'R', bats: 'R', height: '6-7', weight: 282 },
    { mlbId: 673962, firstName: 'Juan', lastName: 'Soto', position: 'LF', number: 22, throws: 'L', bats: 'L', height: '6-2', weight: 224 },
    { mlbId: 656801, firstName: 'Alex', lastName: 'Verdugo', position: 'LF', number: 24, throws: 'L', bats: 'L', height: '6-0', weight: 192 },
    { mlbId: 543829, firstName: 'Giancarlo', lastName: 'Stanton', position: 'DH', number: 27, throws: 'R', bats: 'R', height: '6-6', weight: 245 },
    { mlbId: 669720, firstName: 'Trent', lastName: 'Grisham', position: 'CF', number: 12, throws: 'L', bats: 'L', height: '6-0', weight: 224 },
    { mlbId: 680757, firstName: 'Jasson', lastName: 'Dominguez', position: 'CF', number: 89, throws: 'R', bats: 'S', height: '5-10', weight: 190 },
  ],
  orioles: [
    // Pitchers
    { mlbId: 669302, firstName: 'Corbin', lastName: 'Burnes', position: 'SP', number: 39, throws: 'R', bats: 'R', height: '6-3', weight: 225 },
    { mlbId: 669203, firstName: 'Grayson', lastName: 'Rodriguez', position: 'SP', number: 30, throws: 'R', bats: 'L', height: '6-5', weight: 220 },
    { mlbId: 669222, firstName: 'Kyle', lastName: 'Bradish', position: 'SP', number: 56, throws: 'R', bats: 'R', height: '6-4', weight: 215 },
    { mlbId: 592789, firstName: 'Dean', lastName: 'Kremer', position: 'SP', number: 64, throws: 'R', bats: 'R', height: '6-2', weight: 180 },
    { mlbId: 676872, firstName: 'Cade', lastName: 'Povich', position: 'SP', number: 47, throws: 'L', bats: 'L', height: '6-4', weight: 205 },
    { mlbId: 606149, firstName: 'Craig', lastName: 'Kimbrel', position: 'RP', number: 46, throws: 'R', bats: 'R', height: '5-11', weight: 210 },
    { mlbId: 669923, firstName: 'Yennier', lastName: 'Cano', position: 'RP', number: 77, throws: 'R', bats: 'R', height: '6-2', weight: 200 },
    { mlbId: 666176, firstName: 'Danny', lastName: 'Coulombe', position: 'RP', number: 32, throws: 'L', bats: 'L', height: '5-10', weight: 185 },
    // Catchers
    { mlbId: 608665, firstName: 'Adley', lastName: 'Rutschman', position: 'C', number: 35, throws: 'R', bats: 'S', height: '6-2', weight: 220 },
    { mlbId: 608384, firstName: 'James', lastName: 'McCann', position: 'C', number: 27, throws: 'R', bats: 'R', height: '6-3', weight: 220 },
    // Infielders
    { mlbId: 680776, firstName: 'Gunnar', lastName: 'Henderson', position: 'SS', number: 2, throws: 'R', bats: 'L', height: '6-3', weight: 210 },
    { mlbId: 683002, firstName: 'Jackson', lastName: 'Holliday', position: '2B', number: 7, throws: 'R', bats: 'L', height: '6-1', weight: 175 },
    { mlbId: 669357, firstName: 'Ryan', lastName: 'Mountcastle', position: '1B', number: 6, throws: 'R', bats: 'R', height: '6-3', weight: 225 },
    { mlbId: 672356, firstName: 'Jordan', lastName: 'Westburg', position: '2B', number: 11, throws: 'R', bats: 'R', height: '6-3', weight: 203 },
    { mlbId: 669065, firstName: 'Ramon', lastName: 'Urias', position: '3B', number: 29, throws: 'R', bats: 'R', height: '5-10', weight: 185 },
    // Outfielders
    { mlbId: 645302, firstName: 'Cedric', lastName: 'Mullins', position: 'CF', number: 31, throws: 'L', bats: 'L', height: '5-8', weight: 175 },
    { mlbId: 668939, firstName: 'Anthony', lastName: 'Santander', position: 'RF', number: 25, throws: 'R', bats: 'S', height: '6-2', weight: 235 },
    { mlbId: 642136, firstName: 'Colton', lastName: 'Cowser', position: 'LF', number: 17, throws: 'R', bats: 'L', height: '6-3', weight: 195 },
    { mlbId: 666971, firstName: 'Heston', lastName: 'Kjerstad', position: 'RF', number: 13, throws: 'R', bats: 'L', height: '6-3', weight: 205 },
    { mlbId: 669401, firstName: 'Austin', lastName: 'Hays', position: 'LF', number: 21, throws: 'R', bats: 'R', height: '5-10', weight: 200 },
  ],
  tigers: [
    // Pitchers (includes the ones in mlbHeadshotService)
    { mlbId: 669373, firstName: 'Tarik', lastName: 'Skubal', position: 'SP', number: 29, throws: 'L', bats: 'L', height: '6-3', weight: 215 },
    { mlbId: 656427, firstName: 'Jack', lastName: 'Flaherty', position: 'SP', number: 22, throws: 'R', bats: 'R', height: '6-4', weight: 225 },
    { mlbId: 663554, firstName: 'Casey', lastName: 'Mize', position: 'SP', number: 12, throws: 'R', bats: 'R', height: '6-3', weight: 220 },
    { mlbId: 641771, firstName: 'Reese', lastName: 'Olson', position: 'SP', number: 45, throws: 'R', bats: 'R', height: '6-1', weight: 180 },
    { mlbId: 677651, firstName: 'Keider', lastName: 'Montero', position: 'SP', number: 36, throws: 'R', bats: 'R', height: '6-0', weight: 180 },
    { mlbId: 656024, firstName: 'Jason', lastName: 'Foley', position: 'RP', number: 68, throws: 'R', bats: 'R', height: '6-4', weight: 215 },
    { mlbId: 650556, firstName: 'Tyler', lastName: 'Holton', position: 'RP', number: 57, throws: 'L', bats: 'R', height: '6-2', weight: 200 },
    // Catchers
    { mlbId: 641598, firstName: 'Jake', lastName: 'Rogers', position: 'C', number: 34, throws: 'R', bats: 'R', height: '6-1', weight: 190 },
    { mlbId: 678882, firstName: 'Dillon', lastName: 'Dingler', position: 'C', number: 32, throws: 'R', bats: 'R', height: '6-3', weight: 210 },
    // Infielders
    { mlbId: 666158, firstName: 'Spencer', lastName: 'Torkelson', position: '1B', number: 20, throws: 'R', bats: 'R', height: '6-1', weight: 220 },
    { mlbId: 656775, firstName: 'Colt', lastName: 'Keith', position: '2B', number: 33, throws: 'R', bats: 'L', height: '6-2', weight: 205 },
    { mlbId: 663993, firstName: 'Javier', lastName: 'Baez', position: 'SS', number: 28, throws: 'R', bats: 'R', height: '6-0', weight: 190 },
    { mlbId: 666156, firstName: 'Gio', lastName: 'Urshela', position: '3B', number: 13, throws: 'R', bats: 'R', height: '6-0', weight: 215 },
    // Outfielders
    { mlbId: 682668, firstName: 'Riley', lastName: 'Greene', position: 'CF', number: 31, throws: 'L', bats: 'L', height: '6-3', weight: 200 },
    { mlbId: 672580, firstName: 'Parker', lastName: 'Meadows', position: 'LF', number: 22, throws: 'L', bats: 'L', height: '6-5', weight: 205 },
    { mlbId: 676724, firstName: 'Kerry', lastName: 'Carpenter', position: 'DH', number: 30, throws: 'L', bats: 'L', height: '6-2', weight: 225 },
    { mlbId: 664702, firstName: 'Matt', lastName: 'Vierling', position: 'RF', number: 8, throws: 'R', bats: 'R', height: '6-3', weight: 205 },
  ],
  athletics: [
    // Pitchers
    { mlbId: 686610, firstName: 'JP', lastName: 'Sears', position: 'SP', number: 38, throws: 'L', bats: 'L', height: '5-11', weight: 180 },
    { mlbId: 666200, firstName: 'Paul', lastName: 'Blackburn', position: 'SP', number: 58, throws: 'R', bats: 'R', height: '6-1', weight: 195 },
    { mlbId: 668881, firstName: 'Joey', lastName: 'Estes', position: 'SP', number: 37, throws: 'R', bats: 'R', height: '6-2', weight: 190 },
    { mlbId: 666157, firstName: 'Ross', lastName: 'Stripling', position: 'SP', number: 48, throws: 'R', bats: 'R', height: '6-3', weight: 220 },
    { mlbId: 543294, firstName: 'Hogan', lastName: 'Harris', position: 'SP', number: 64, throws: 'L', bats: 'L', height: '6-3', weight: 230 },
    { mlbId: 666185, firstName: 'Mason', lastName: 'Miller', position: 'RP', number: 19, throws: 'R', bats: 'R', height: '6-5', weight: 200 },
    { mlbId: 656756, firstName: 'Lucas', lastName: 'Erceg', position: 'RP', number: 57, throws: 'R', bats: 'R', height: '6-3', weight: 200 },
    // Catchers
    { mlbId: 680748, firstName: 'Shea', lastName: 'Langeliers', position: 'C', number: 23, throws: 'R', bats: 'R', height: '6-0', weight: 205 },
    { mlbId: 543510, firstName: 'Kyle', lastName: 'McCann', position: 'C', number: 12, throws: 'R', bats: 'L', height: '6-2', weight: 210 },
    // Infielders
    { mlbId: 680687, firstName: 'Zack', lastName: 'Gelof', position: '2B', number: 20, throws: 'R', bats: 'R', height: '6-0', weight: 205 },
    { mlbId: 670868, firstName: 'Tyler', lastName: 'Soderstrom', position: '1B', number: 21, throws: 'R', bats: 'L', height: '6-2', weight: 200 },
    { mlbId: 680573, firstName: 'Lawrence', lastName: 'Butler', position: '1B', number: 4, throws: 'R', bats: 'L', height: '6-1', weight: 225 },
    { mlbId: 677800, firstName: 'Jacob', lastName: 'Wilson', position: 'SS', number: 6, throws: 'R', bats: 'R', height: '6-3', weight: 180 },
    { mlbId: 672386, firstName: 'Nick', lastName: 'Allen', position: 'SS', number: 2, throws: 'R', bats: 'R', height: '5-8', weight: 166 },
    // Outfielders
    { mlbId: 682626, firstName: 'JJ', lastName: 'Bleday', position: 'RF', number: 33, throws: 'L', bats: 'L', height: '6-3', weight: 205 },
    { mlbId: 671272, firstName: 'Brent', lastName: 'Rooker', position: 'DH', number: 25, throws: 'R', bats: 'R', height: '6-3', weight: 225 },
    { mlbId: 643327, firstName: 'Seth', lastName: 'Brown', position: 'LF', number: 15, throws: 'L', bats: 'L', height: '6-1', weight: 223 },
    { mlbId: 687799, firstName: 'Colby', lastName: 'Thomas', position: 'CF', number: 1, throws: 'R', bats: 'R', height: '6-2', weight: 195 },
  ],
};

// =============================================================================
// ROSTER LOOKUP FUNCTIONS
// =============================================================================

/**
 * Get full roster for an MLB team with headshot URLs.
 *
 * @param {string} teamKey - Team identifier (e.g., 'yankees', 'orioles')
 * @returns {Array} - Array of player objects with headshot URLs
 */
export function getMLBTeamRoster(teamKey) {
  const normalizedKey = teamKey?.toLowerCase();
  // Try direct key first, then abbreviation mapping
  const actualKey = MLB_ROSTERS[normalizedKey] ? normalizedKey : MLB_ABBREV_TO_KEY[normalizedKey];
  const roster = MLB_ROSTERS[actualKey];
  const team = MLB_TEAMS[actualKey];

  if (!roster || !team) {
    console.warn(`[MLBRoster] Team not found: ${teamKey}`);
    return [];
  }

  // Enrich each player with headshot URL and team info
  return roster.map((player, index) => ({
    id: `${team.abbreviation}-${player.mlbId}`,
    player_id: player.mlbId,
    mlbId: player.mlbId,
    first_name: player.firstName,
    last_name: player.lastName,
    name: `${player.firstName} ${player.lastName}`,
    position: player.position,
    position_abbreviation: player.position,
    number: player.number,
    throws: player.throws,
    bats: player.bats,
    height: player.height,
    weight: player.weight,
    team: team.name,
    team_id: team.teamId,
    team_abbreviation: team.abbreviation,
    // Use MLB headshot service for image URL
    image_url: getMLBHeadshotUrl(player.mlbId, { size: 'MEDIUM' }),
    photo: getMLBHeadshotUrl(player.mlbId, { size: 'LARGE' }),
    imageUrl: getMLBHeadshotUrl(player.mlbId, { size: 'MEDIUM' }),
    // Generated stats for demo (would come from Statcast in production)
    stats: generateDemoStats(player.position, player.mlbId),
    ...generateDemoStats(player.position, player.mlbId),
  }));
}

/**
 * Get team info by teamKey
 */
export function getMLBTeamInfo(teamKey) {
  return MLB_TEAMS[teamKey?.toLowerCase()] || null;
}

/**
 * Check if team is an MLB team
 */
export function isMLBTeam(teamKey) {
  return teamKey?.toLowerCase() in MLB_TEAMS;
}

/**
 * Get all available MLB teams
 */
export function getAvailableMLBTeams() {
  return Object.entries(MLB_TEAMS).map(([key, team]) => ({
    team_id: key,
    team_name: team.name,
    abbreviation: team.abbreviation,
    logo_url: team.logoUrl,
    team_color: team.primaryColor,
    mlbTeamId: team.mlbTeamId,
    division: team.division,
    league: team.league,
  }));
}

/**
 * Search players across all MLB teams
 */
export function searchMLBPlayers(query) {
  const normalizedQuery = query?.toLowerCase() || '';
  const results = [];

  Object.entries(MLB_ROSTERS).forEach(([teamKey, roster]) => {
    const team = MLB_TEAMS[teamKey];
    roster.forEach(player => {
      const fullName = `${player.firstName} ${player.lastName}`.toLowerCase();
      if (fullName.includes(normalizedQuery) || player.lastName.toLowerCase().includes(normalizedQuery)) {
        results.push({
          ...player,
          name: `${player.firstName} ${player.lastName}`,
          team: team.name,
          team_id: teamKey,
          imageUrl: getMLBHeadshotUrl(player.mlbId, { size: 'MEDIUM' }),
        });
      }
    });
  });

  return results;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate realistic baseball stats based on position
 * These are placeholder stats for UI display - real data comes from Statcast
 *
 * @param {string} position - Player's position (SP, RP, C, 1B, etc.)
 * @param {number} mlbId - Player's MLB ID for deterministic seeding
 * @returns {Object} Baseball stats appropriate for the position
 */
function generateDemoStats(position, mlbId = 0) {
  // Seeded random for consistent stats per player
  const seed = mlbId || Math.floor(Math.random() * 10000);
  const seededRandom = (min, max) => {
    const x = Math.sin(seed * (min + max)) * 10000;
    return min + (x - Math.floor(x)) * (max - min);
  };

  const isPitcher = ['SP', 'RP', 'P', 'RHP', 'LHP'].includes(position?.toUpperCase());

  if (isPitcher) {
    // Pitcher stats
    const era = seededRandom(2.5, 4.5).toFixed(2);
    const whip = seededRandom(1.0, 1.35).toFixed(2);
    const kPer9 = seededRandom(8.0, 12.0).toFixed(1);
    const wins = Math.floor(seededRandom(5, 15));
    const losses = Math.floor(seededRandom(3, 10));
    const strikeouts = Math.floor(seededRandom(80, 220));
    const innings = seededRandom(100, 200).toFixed(1);

    return {
      // Core stats
      era: parseFloat(era),
      ERA: era,
      whip: parseFloat(whip),
      WHIP: whip,
      kPer9: parseFloat(kPer9),
      wins: wins,
      losses: losses,
      strikeouts: strikeouts,
      innings: parseFloat(innings),
      // Statcast metrics
      avgFastball: seededRandom(92, 98).toFixed(1),
      spinRate: Math.floor(seededRandom(2100, 2600)),
      whiffRate: seededRandom(22, 35).toFixed(1),
      chaseRate: seededRandom(28, 38).toFixed(1),
      // For UI display
      batting_average: null,
      home_runs: null,
      rbi: null,
      ops: null,
    };
  } else {
    // Position player (hitter) stats
    const avg = seededRandom(0.220, 0.310).toFixed(3);
    const obp = seededRandom(0.300, 0.400).toFixed(3);
    const slg = seededRandom(0.380, 0.550).toFixed(3);
    const ops = (parseFloat(obp) + parseFloat(slg)).toFixed(3);
    const hr = Math.floor(seededRandom(8, 35));
    const rbi = Math.floor(seededRandom(40, 110));
    const hits = Math.floor(seededRandom(100, 180));
    const runs = Math.floor(seededRandom(50, 100));

    return {
      // Core stats
      batting_average: avg,
      AVG: avg,
      obp: parseFloat(obp),
      OBP: obp,
      slg: parseFloat(slg),
      SLG: slg,
      ops: parseFloat(ops),
      OPS: ops,
      home_runs: hr,
      rbi: rbi,
      hits: hits,
      runs: runs,
      // Statcast metrics
      exitVelocity: seededRandom(86, 95).toFixed(1),
      launchAngle: seededRandom(10, 18).toFixed(1),
      barrelRate: seededRandom(5, 15).toFixed(1),
      hardHitRate: seededRandom(35, 50).toFixed(1),
      sprintSpeed: seededRandom(26, 30).toFixed(1),
      // For UI display
      era: null,
      wins: null,
      strikeouts: null,
    };
  }
}

// =============================================================================
// DYNAMIC ROSTER FETCHING (MLB Stats API Fallback)
// =============================================================================

/**
 * Fetch MLB team roster dynamically from MLB Stats API.
 * Uses local cache if available, otherwise fetches from API.
 *
 * @param {string} teamKey - Team identifier (e.g., 'yankees', 'orioles', 'ari', 'nyy')
 * @returns {Promise<Array>} - Array of player objects with headshot URLs
 */
export async function fetchMLBTeamRoster(teamKey) {
  // First try local roster (fast path)
  const localRoster = getMLBTeamRoster(teamKey);
  if (localRoster && localRoster.length > 0) {
    console.log(`[MLBRoster] Using local roster for ${teamKey}: ${localRoster.length} players`);
    return localRoster;
  }

  // Resolve team info via abbreviation mapping
  const normalizedKey = teamKey?.toLowerCase();
  const actualKey = MLB_ABBREV_TO_KEY[normalizedKey] || normalizedKey;
  const team = MLB_TEAMS[actualKey];

  if (!team) {
    console.warn(`[MLBRoster] Team not found: ${teamKey}`);
    return [];
  }

  // Dynamic fetch from MLB Stats API
  console.log(`[MLBRoster] Fetching roster from MLB API for ${team.name} (ID: ${team.mlbTeamId})`);

  try {
    const response = await fetch(
      `https://statsapi.mlb.com/api/v1/teams/${team.mlbTeamId}/roster?rosterType=40Man`,
      { signal: AbortSignal.timeout(10000) } // 10s timeout
    );

    if (!response.ok) {
      throw new Error(`MLB API returned ${response.status}`);
    }

    const data = await response.json();
    const roster = data.roster || [];

    console.log(`[MLBRoster] Fetched ${roster.length} players from MLB API for ${team.name}`);

    // Transform MLB API format to our internal format
    return roster.map((entry, index) => {
      const player = entry.person;
      const position = entry.position?.abbreviation || 'UTIL';
      const [firstName, ...lastNameParts] = (player.fullName || 'Unknown Player').split(' ');
      const lastName = lastNameParts.join(' ') || firstName;

      return {
        id: `${team.abbreviation}-${player.id}`,
        player_id: player.id,
        mlbId: player.id,
        first_name: firstName,
        last_name: lastName,
        name: player.fullName,
        position: position,
        position_abbreviation: position,
        number: entry.jerseyNumber || '',
        throws: player.pitchHand?.code || '',
        bats: player.batSide?.code || '',
        height: player.height || '',
        weight: player.weight || '',
        team: team.name,
        team_id: team.teamId,
        team_abbreviation: team.abbreviation,
        // Use MLB headshot service for image URL
        image_url: getMLBHeadshotUrl(player.id, { size: 'MEDIUM' }),
        photo: getMLBHeadshotUrl(player.id, { size: 'LARGE' }),
        imageUrl: getMLBHeadshotUrl(player.id, { size: 'MEDIUM' }),
        // Generated stats for demo (would come from Statcast in production)
        stats: generateDemoStats(position, player.id),
        ...generateDemoStats(position, player.id),
      };
    });
  } catch (error) {
    console.error(`[MLBRoster] MLB API fetch failed for ${team.name}:`, error.message);
    // Return empty array on failure - caller can handle gracefully
    return [];
  }
}

/**
 * Get position group for filtering
 */
export function getPositionGroup(position) {
  const pos = position?.toUpperCase();
  if (['SP', 'RP', 'P'].includes(pos)) return 'Pitchers';
  if (['C'].includes(pos)) return 'Catchers';
  if (['1B', '2B', '3B', 'SS'].includes(pos)) return 'Infielders';
  if (['LF', 'CF', 'RF', 'OF'].includes(pos)) return 'Outfielders';
  if (['DH'].includes(pos)) return 'Designated Hitters';
  return 'Other';
}

/**
 * Get available position filters for baseball
 */
export function getBaseballPositionFilters() {
  return [
    { key: 'all', label: 'All Positions' },
    { key: 'pitchers', label: 'Pitchers', positions: ['SP', 'RP', 'P'] },
    { key: 'catchers', label: 'Catchers', positions: ['C'] },
    { key: 'infielders', label: 'Infielders', positions: ['1B', '2B', '3B', 'SS'] },
    { key: 'outfielders', label: 'Outfielders', positions: ['LF', 'CF', 'RF', 'OF'] },
    { key: 'dh', label: 'DH', positions: ['DH'] },
  ];
}

export default {
  getMLBTeamRoster,
  fetchMLBTeamRoster,
  getMLBTeamInfo,
  isMLBTeam,
  getAvailableMLBTeams,
  searchMLBPlayers,
  getPositionGroup,
  getBaseballPositionFilters,
  MLB_TEAMS,
};
